/**
 * Dutch document strings.
 *
 * Accounting wording follows `docs/ACCOUNTING-TERMINOLOGY-GLOSSARY.md`
 * (factuur — never "rekening" — BTW, verlegd, excl./incl. BTW); strings that
 * already exist in the app's `common` namespace are copied from
 * `apps/web/public/locales/nl/common.json` verbatim.
 */
export const nl = {
    common: {
        generatedOn: 'Gegenereerd op {{date}}',
        page: 'Pagina {{page}} van {{total}}',
        status: 'Status',
        project: 'Project',
    },
    identity: {
        iban: 'IBAN',
        bic: 'BIC',
        tradingName: 'Handelsnaam',
        relatedParty: 'Verbonden partij',
        registrationNumber: {
            NL: 'KvK-nummer',
            BE: 'BCE/KBO-nummer',
            DE: 'Handelsregister',
            FR: 'SIREN',
            GB: 'Bedrijfsnummer',
            US: 'Staatsregistratie',
            DEFAULT: 'Registratienummer',
        },
        vatNumber: {
            NL: 'BTW-nummer',
            BE: 'BTW-nummer',
            DE: 'USt-IdNr',
            FR: 'BTW-nummer',
            GB: 'BTW-nummer',
            US: 'Omzetbelastingnummer',
            DEFAULT: 'BTW-nummer',
        },
        taxEntityNumber: {
            NL: 'RSIN',
            US: 'EIN',
            DEFAULT: 'Fiscaal nummer',
        },
        taxExempt: {
            NL: 'ANBI-status',
            GB: 'Goededoelenstatus',
            US: '501(c)(3)-status',
            DEFAULT: 'Vrijgesteld van belasting',
        },
    },
    invoice: {
        titleAr: 'Factuur',
        titleArDraft: 'Conceptfactuur',
        draftNotice: 'Dit is geen factuur. Aan dit document kan geen recht op aftrek van btw worden ontleend.',
        cancelledNotice: 'Geannuleerd — deze factuur is tegengeboekt en er is niets op verschuldigd. Dit is een afschrift van de geannuleerde factuur, geen creditnota.',
        titleAp: 'Inkoopfactuur',
        from: 'Van',
        billTo: 'Factureren aan',
        billFrom: 'Factuur van',
        details: 'Factuurgegevens',
        draftDetails: 'Gegevens',
        invoiceDate: 'Factuurdatum',
        draftDate: 'Datum',
        supplyDate: 'Leverdatum',
        dueDate: 'Vervaldatum',
        cancelledOn: 'Geannuleerd op',
        paymentTerms: 'Betalingsvoorwaarden',
        noteToCustomer: 'Opmerking voor klant',
        selfBilling: 'Self-billing',
        vatNotes: 'BTW-opmerkingen',
        lines: {
            heading: 'Product of Dienst',
            description: 'Omschrijving',
            serviceCategory: 'Dienstcategorie',
            qty: 'Aantal',
            unitPrice: 'Prijs',
            lineTotal: 'Subtotaal',
            total: 'Totaal',
        },
        treatments: {
            exempt: 'Vrijgesteld',
            reverse_charge: 'BTW verlegd',
            zero_rated: 'Nultarief',
            non_reclaimable: 'Btw niet aftrekbaar',
        },
        taxStatements: {
            reverse_charge: 'BTW verlegd',
            non_reclaimable: 'Totaal inclusief niet-aftrekbare btw',
        },
        exemptionReasons: {
            art_132_1_i: 'Vrijgesteld van BTW op grond van artikel 132(1)(i) van de BTW-richtlijn',
            art_132_1_n: 'Vrijgesteld van BTW op grond van artikel 132(1)(n) van de BTW-richtlijn',
            art_132_1_j: 'Vrijgesteld van BTW op grond van artikel 132(1)(j) van de BTW-richtlijn',
            export: 'Nultarief export op grond van artikel 146 van de BTW-richtlijn',
            intra_eu: 'Nultarief intracommunautaire levering op grond van artikel 138 van de BTW-richtlijn',
            resale: 'Vrijgesteld — wederverkoopcertificaat aanwezig',
            nonprofit: 'Vrijgesteld — non-profitorganisatie',
            government: 'Vrijgesteld — overheidsinstelling',
        },
        totals: {
            subtotal: 'Subtotaal excl. BTW',
            total: 'Totaal incl. BTW',
            amountPaid: 'Betaald bedrag',
            amountDue: 'Openstaand bedrag',
        },
        payments: {
            heading: 'Betalingsgeschiedenis',
            entry: 'Betaling #{{index}}',
        },
        statuses: {
            draft: 'Concept',
            sent: 'Verzonden',
            unpaid: 'Onbetaald',
            partially_paid: 'Deels betaald',
            paid: 'Betaald',
            overdue: 'Achterstallig',
            cancelled: 'Geannuleerd',
            pending: 'In behandeling',
            approved: 'Goedgekeurd',
            rejected: 'Afgewezen',
            on_hold: 'In de wacht',
        },
        thanks: 'Bedankt voor uw opdracht!',
    },
    report: {
        asOf: 'Per {{date}}',
        forPeriod: 'Voor de periode {{start}} – {{end}}',
        amountsIn: 'Alle bedragen in {{currency}}',
        managementLegend: 'Managementrapportage — niet gecontroleerd',
        ledger: {
            unattributedCurrency: 'Valuta niet toe te wijzen',
            unattributedNote: 'Regels op de valutatussenrekening die tegenover meer dan één valuta staan, zijn niet aan één valuta toe te wijzen. Ze staan apart, zonder valutateken.',
            filteredFunds: 'Gefilterd op {{count}} fonds(en)',
            unallocatedOnly: 'Alleen niet toegewezen — regels zonder fonds',
            fundTaggingDifference: 'Verschil door fondstoewijzing',
            fundTaggingNote: 'Met een fondsfilter kan een journaalpost een deel van haar regels verliezen (de bankregel heeft geen fonds, de kostenregel wel). Het verschil door fondstoewijzing is dat gat, getoond zodat de kolommen sluiten.',
            account: 'Rekening',
            debit: 'Debet',
            credit: 'Credit',
            total: 'Totaal',
        },
        trialBalance: {
            title: 'Proef- en saldibalans',
            opening: 'Beginsaldo',
            movement: 'Mutaties',
            closing: 'Eindsaldo',
            categories: {
                assets: 'Activa',
                liabilities: 'Schulden',
                equity: 'Eigen vermogen',
                revenue: 'Opbrengsten',
                expenses: 'Kosten',
            },
            priorResult: 'Resultaat t/m {{date}} (vóór resultaatbestemming)',
            empty: 'Geen boekingen in deze periode.',
            notBalancedTitle: 'Deze proef- en saldibalans sluit niet',
            notBalancedLine: '{{column}}: debet {{debit}}, credit {{credit}}',
            notBalancedBody: 'Het totaal debet en het totaal credit horen gelijk te zijn. Controleer het grootboek op journaalposten die niet in evenwicht zijn.',
            fxClearingNote: 'Valutatussenrekeningen staan hier; de balans laat ze weg.',
            suspenseNote: 'Tussenrekeningen staan hier en op de balans.',
            suspenseWarningTitle: 'Saldo tussenrekening: {{amount}}',
            suspenseWarningBody: 'Dit zijn nog niet gecategoriseerde boekingen. Categoriseer ze voordat u op deze cijfers vertrouwt.',
            workingDocumentNote: 'Opbrengsten- en kostenrekeningen beginnen op nul; hun resultaat vóór de periode staat op één regel onder eigen vermogen. Een proef- en saldibalans is een werkdocument, geen wettelijk overzicht.',
        },
        cashFlow: {
            title: 'Kasstroomoverzicht (directe methode)',
            activities: {
                operating: 'Kasstroom uit operationele activiteiten',
                investing: 'Kasstroom uit investeringsactiviteiten',
                financing: 'Kasstroom uit financieringsactiviteiten',
            },
            netActivities: {
                operating: 'Totaal kasstroom uit operationele activiteiten',
                investing: 'Totaal kasstroom uit investeringsactiviteiten',
                financing: 'Totaal kasstroom uit financieringsactiviteiten',
            },
            sections: {
                operating_customers: 'Ontvangsten van klanten en subsidiegevers',
                operating_revenue: 'Overige ontvangsten uit opbrengsten',
                operating_suppliers: 'Betalingen aan leveranciers',
                operating_expenses: 'Betaalde kosten',
                operating_interest_received: 'Ontvangen interest',
                operating_interest_paid: 'Betaalde interest',
                operating_income_tax: 'Betaalde winstbelasting',
                operating_vat_taxes: 'Btw en overige belastingen',
                operating_payroll_other: 'Salarissen en overige kortlopende schulden',
                operating_other_current_assets: 'Overige vlottende activa',
                operating_credit_cards: 'Afrekeningen creditcards',
                operating_deferred_revenue: 'Vooruitontvangen opbrengsten',
                operating_deposits_provisions: 'Waarborgsommen en voorzieningen',
                investing: 'Aan- en verkoop van vaste activa',
                financing: 'Leningen, eigen vermogen en rekening-courant eigenaren',
                outside_fx_transfers: 'Mutaties via de valutatussenrekening (overboekingen tussen eigen rekeningen in verschillende valuta en afrekeningen in een andere valuta)',
                cash: 'Overige bank- en kasrekeningen',
                outside_opening_balances: 'Geboekte beginbalansen',
                outside_suspense: 'Tussenrekening (niet gecategoriseerd)',
                unattributed: 'Valuta niet toe te wijzen',
                unmapped: 'Niet-ingedeelde rekeningen',
                fund_tagging_difference: 'Kasstromen van andere fondsen en niet-toegewezen regels',
            },
            directions: {
                investing: { outflow: 'Investeringen', inflow: 'Desinvesteringen' },
                financing: { outflow: 'Aflossingen en overige betalingen', inflow: 'Opnames en overige ontvangsten' },
            },
            otherSection: 'Overig ({{key}})',
            netCashFromActivities: 'Kasstroom uit activiteiten',
            reconciling: 'Aansluitposten buiten de activiteiten',
            totalReconciling: 'Totaal aansluitposten',
            netChange: 'Mutatie liquide middelen',
            openingCash: 'Liquide middelen begin periode',
            closingCash: 'Liquide middelen einde periode',
            openingCashAt: 'Liquide middelen per {{date}}',
            closingCashAt: 'Liquide middelen per {{date}}',
            empty: 'Geen bankrekeningen of kasstromen in deze periode.',
            notReconciledTitle: 'Beginsaldo plus mutatie is niet gelijk aan het eindsaldo liquide middelen',
            notReconciledPriorTitle: 'Vorige periode {{start}} – {{end}}: beginsaldo plus mutatie is niet gelijk aan het eindsaldo liquide middelen',
            notReconciledBody: 'Controleer het grootboek op journaalposten die geld verplaatsen zonder tegenregel.',
            comparativeUnavailableTitle: 'Vergelijkende periode {{start}} – {{end}} kon niet worden opgehaald',
            comparativeUnavailableBody: 'Dit overzicht toont alleen de huidige periode. De getoonde cijfers zijn volledig voor die periode.',
            notes: {
                cash: 'Liquide middelen zijn de saldi van de bank- en kasrekeningen, ook wanneer een rekening rood staat. Een aparte rekening-courantkrediet staat onder financieringsactiviteiten.',
                nonCash: 'Transacties zonder geldbeweging zijn weggelaten.',
                vatAndCards: 'Btw of verkoopbelasting op een verkoop of aankoop staat bij de operationele post die in dezelfde transactie is betaald of ontvangen; is die er niet (een vast actief, een lening, rente of winstbelasting), dan staat de belasting onder betalingen aan leveranciers of ontvangsten van klanten, zodat investerings- en financieringsactiviteiten exclusief btw zijn. Btw en overige belastingen toont betalingen en teruggaven op btw- of verkoopbelastingaangiften en betalingen van overige belastingen. Een creditcardaankoop verschijnt wanneer de creditcard wordt afgerekend.',
                lookThrough: 'Een betaling van een factuur wordt ingedeeld naar de regels van die factuur: het deel voor een vast actief, een belegging, een lening of andere financiering, rente of winstbelasting staat onder die activiteit; de rest, btw inbegrepen, blijft onder ontvangsten van klanten of betalingen aan leveranciers. Creditcardafrekeningen en uitbetalingen van verkoopkanalen worden niet zo verdeeld en blijven op hun eigen regel.',
                disposals: 'Winsten en verliezen op de verkoop van een vast actief staan onder investeringsactiviteiten, zodat de opbrengst van een verkoop volledig is.',
                fxClearing: 'Een betaling vanaf een bankrekening in een andere valuta (bijvoorbeeld een lening afgelost vanaf de USD-rekening) staat onder de mutaties via de valutatussenrekening in de betalende valuta, en onder haar activiteit in de valuta van de rekening.',
                fundTaggingDifference: 'Begin- en eindsaldo liquide middelen zijn die van de hele organisatie. Met een fondsfilter is deze regel de kasstroom van alle andere fondsen en van regels zonder fonds, zodat het overzicht toch aansluit; het wijst niet op een verkeerde toewijzing. Het toewijzen van de bankregel aan een fonds maakt geen verschil.',
            },
        },
        openItems: {
            title: { ar: 'Debiteurenlijst', ap: 'Crediteurenlijst' },
            contact: { ar: 'Klant', ap: 'Leverancier' },
            invoice: 'Factuur',
            invoiceDate: 'Factuurdatum',
            dueDate: 'Vervaldatum',
            daysOverdue: 'Dagen vervallen',
            invoiceTotal: 'Factuurbedrag',
            outstanding: 'Openstaand',
            noContact: 'Geen contact',
            contactTotal: 'Totaal {{name}}',
            agingTitle: 'Ouderdom',
            buckets: {
                current: 'Niet vervallen',
                '1-30': '1–30 dagen',
                '31-60': '31–60 dagen',
                '61-90': '61–90 dagen',
                '90+': 'Meer dan 90 dagen',
            },
            total: 'Totaal',
            subtotals: {
                ar: {
                    debitBalances: 'Debetsaldi',
                    creditBalances: 'Creditsaldi (te veel ontvangen)',
                    net: 'Per saldo te ontvangen',
                },
                ap: {
                    debitBalances: 'Debetsaldi (te veel betaald)',
                    creditBalances: 'Creditsaldi',
                    net: 'Per saldo te betalen',
                },
            },
            tieOut: {
                title: 'Aansluiting met het grootboek',
                account: 'Grootboekrekening',
                listNet: 'Openstaande posten',
                openingBalances: 'Beginbalansen',
                manualJournals: 'Overige journaalposten',
                ledgerBalance: 'Grootboek',
                unexplained: 'Onverklaard',
                unexplainedTitle: 'Een deel van het grootboeksaldo is onverklaard',
                unexplainedBody: 'De openstaande posten, beginbalansen en overige journaalposten tellen niet op tot het grootboeksaldo. Controleer de grootboekrekening.',
            },
            empty: 'Geen openstaande posten per {{date}}.',
            ledgerNote: 'Opgesteld uit het grootboek: een factuur staat open wanneer de regels die er tot deze datum op geboekt zijn niet op nul uitkomen, ongeacht de status vandaag. De ouderdom op het dashboard is een managementweergave op basis van de status en kan afwijken.',
            editLimitNote: 'Een gewijzigde uitgegeven factuur wordt op dezelfde plek opnieuw geboekt, dus een lijst voor een eerdere datum kan achteraf veranderen.',
        },
        balanceSheet: {
            title: 'Balans',
            assets: 'Activa',
            currentAssets: 'Vlottende activa',
            nonCurrentAssets: 'Vaste activa',
            totalCurrentAssets: 'Totaal vlottende activa',
            totalNonCurrentAssets: 'Totaal vaste activa',
            totalAssets: 'Totaal activa',
            liabilitiesAndEquity: 'Passiva en eigen vermogen',
            currentLiabilities: 'Kortlopende schulden',
            nonCurrentLiabilities: 'Langlopende schulden',
            totalCurrentLiabilities: 'Totaal kortlopende schulden',
            totalNonCurrentLiabilities: 'Totaal langlopende schulden',
            totalLiabilities: 'Totaal schulden',
            equity: 'Eigen vermogen',
            totalEquity: 'Totaal eigen vermogen',
            totalLiabilitiesAndEquity: 'Totaal passiva en eigen vermogen',
            beforeAppropriation: 'Vóór resultaatbestemming',
            priorResult: 'Onverdeeld resultaat voorgaande boekjaren',
            yearResult: 'Resultaat boekjaar',
            noCurrentAssets: 'Geen vlottende activa',
            noNonCurrentAssets: 'Geen vaste activa',
            noCurrentLiabilities: 'Geen kortlopende schulden',
            noNonCurrentLiabilities: 'Geen langlopende schulden',
            noEquity: 'Geen eigen vermogen',
            noteTitle: 'Toelichting bij de balans',
            noteBody: 'Er worden geen afsluitboekingen gemaakt. Het eigen vermogen toont daarom afzonderlijk het in Relevé geboekte resultaat van voorgaande boekjaren dat nog niet aan reserves of fondsen is toegevoegd, en het resultaat van het boekjaar vanaf de eerste dag tot de balansdatum. Saldi uit de openingsbalans staan op de rekening Opening Balance Equity.',
            balanceCheck: 'Balanscontrole',
            notBalanced: 'De balans is NIET in evenwicht',
            notBalancedPrior: 'De vergelijkende balans per {{date}} is NIET in evenwicht',
            liabilitiesPlusEquity: 'Passiva + eigen vermogen',
            unexplainedDifference: 'Onverklaard verschil',
            notBalancedBody: 'Dit verschil is geen valuta-omrekeningsverschil — de onderliggende administratie is niet in evenwicht. Controleer recente journaalposten en beginbalansen, of neem contact op met support.',
            missingRatesTitle: 'Ontbrekende wisselkoersen: {{currencies}}',
            missingRatesTitlePrior: 'Ontbrekende wisselkoersen per {{date}}: {{currencies}}',
            missingRatesBody: 'Bedragen in deze valuta worden zonder omrekening (1:1) getoond. Omgerekende totalen en het cumulatieve omrekeningsverschil zijn onbetrouwbaar zolang er geen koersen voor de rapportagedatum beschikbaar zijn.',
            comparativeUnavailableTitle: 'De vergelijkende cijfers per {{date}} konden niet worden opgehaald voor dit rapport',
            comparativeUnavailableBody: 'Het overzicht toont alleen de huidige periode.',
        },
        translation: {
            title: 'Toelichting op valuta-omrekening',
            subtitle: 'Conform IFRS voor MKB sectie 30.25-30.26',
            presentationCurrency: 'Presentatievaluta',
            closingDate: 'Balansdatum',
            method: 'Omrekeningsmethode',
            methodClosingRate: 'Slotkoersmethode (IFRS voor MKB 30.18)',
            functionalCurrencies: 'Omgerekende functionele valuta',
            ratesTitle: 'Toegepaste slotkoersen',
            from: 'Van',
            to: 'Naar',
            rate: 'Koers',
            rateDate: 'Koersdatum',
            source: 'Bron: Europese Centrale Bank (ECB)',
            cta: 'Cumulatief omrekeningsverschil',
            ctaNote: 'Omrekeningsverschillen worden verwerkt in het eigen vermogen conform IFRS voor MKB 30.18(c)',
        },
        incomeStatement: {
            title: 'Resultatenrekening',
            titleSuffixUnallocated: '— Niet-toegewezen transacties',
            titleSuffixFundingSourceView: '— Weergave per financieringsbron',
            titleSuffixFundingSources: '— {{count}} financieringsbronnen',
            revenue: 'Opbrengsten',
            totalRevenue: 'Totaal opbrengsten',
            costOfSales: 'Kostprijs van de omzet',
            totalCostOfSales: 'Totaal kostprijs van de omzet',
            grossProfit: 'Brutowinst',
            operatingExpenses: 'Bedrijfskosten',
            totalOperatingExpenses: 'Totaal bedrijfskosten',
            employeeBenefits: 'Personeelskosten',
            depreciationAmortization: 'Afschrijvingen en amortisatie',
            rawMaterials: 'Grondstoffen en verbruiksgoederen',
            otherOperatingExpenses: 'Overige bedrijfskosten',
            operatingProfit: 'Bedrijfsresultaat',
            financeCosts: 'Financiële lasten',
            totalFinanceCosts: 'Totaal financiële lasten',
            profitBeforeTax: 'Resultaat vóór belastingen',
            taxExpense: 'Belastinglast',
            profitForPeriod: 'Resultaat over de periode',
            netProfitCaption: 'Nettowinst over de periode',
            netLossCaption: 'Nettoverlies over de periode',
            directTransactions: 'Directe transacties',
            account: 'Rekening',
            totalColumn: 'Totaal',
            budget: 'Budget: {{amount}}',
            expenses: 'Kosten',
            totalExpenses: 'Totaal kosten',
            comparisonNote: 'Deze resultatenrekening vergelijkt {{count}} projecten over de periode {{start}} tot {{end}}.',
            periodNote: 'Deze resultatenrekening vat de opbrengsten en kosten samen over de periode {{start}} tot {{end}}.',
            ifrsNote: 'Opgesteld conform IFRS voor MKB sectie 5 (kosten geclassificeerd naar aard)',
            filteredProjects: 'Gefilterd op {{count}} project(en)',
            filteredFunds: 'Gefilterd op {{count}} financieringsbron(nen)',
            unallocatedOnly: 'Alleen niet-toegewezen transacties',
            translationNoteTitle: 'Toelichting valuta-omrekening (IFRS MKB sectie 30)',
            translationNoteBody: 'Bedragen omgerekend naar {{currency}} tegen slotkoersen per {{date}}.',
            translationNoteBodyPrior: 'Vergelijkende bedragen omgerekend naar {{currency}} tegen slotkoersen per {{date}}.',
            translationFunctionalCurrencies: 'Functionele valuta: {{currencies}}',
            translationMissingRates: 'Let op: geen wisselkoersen beschikbaar voor: {{currencies}}',
            translationMissingRatesPrior: 'Let op: geen wisselkoersen beschikbaar voor de vergelijkende periode per {{date}}: {{currencies}}',
            comparativeUnavailableTitle: 'De vergelijkende cijfers over {{start}} — {{end}} konden niet worden opgehaald voor dit rapport',
            comparativeUnavailableBody: 'Het overzicht toont alleen de huidige periode.',
        },
        tax: {
            filingSupportLegend: 'Rapportage ter ondersteuning van de aangifte — niet gecontroleerd',
            vatReturnTitle: '{{tax}}-aangifte',
            salesTaxTitle: 'Verkoopbelastingrapport',
            salesTaxName: 'Verkoopbelasting',
            outputSectionTitle: 'Verschuldigde {{tax}} (verkopen aan klanten)',
            inputSectionTitle: 'Voorbelasting {{tax}} (inkopen bij leveranciers)',
            salesByRateTitle: 'Geheven belasting per tarief',
            outputCard: 'Verschuldigde {{tax}}',
            outputHint: 'Geheven op verkoop',
            inputCard: 'Voorbelasting {{tax}}',
            inputHint: 'Terugvorderbaar op inkopen',
            netCard: 'Netto {{tax}}',
            amountDueLabel: 'Te betalen aan Belastingdienst',
            refundDueLabel: 'Terug te ontvangen van Belastingdienst',
            balancedLabel: 'In evenwicht — niets te verrekenen',
            totalOutput: 'Totaal verschuldigde {{tax}}',
            totalInput: 'Totaal voorbelasting {{tax}}',
            totalTaxCollected: 'Totaal geheven belasting',
            totalSales: 'Totale verkoop',
            taxCollected: 'Geheven belasting',
            rateName: 'Tarief',
            rateColumn: '{{tax}}-tarief',
            rateColumnGeneric: 'Belastingtarief',
            netAmount: 'Nettobedrag',
            grossAmount: 'Brutobedrag',
            taxAmount: '{{tax}}-bedrag',
            taxAmountGeneric: 'Belastingbedrag',
            reverseChargeSuffix: '(btw verlegd)',
            rateSubtotal: 'Subtotaal {{rate}}',
            rateSubtotalExclReverseCharge: 'Subtotaal {{rate}} (excl. verlegde btw)',
            noEntries: 'Geen belastingposten gevonden voor deze periode. Belastinggegevens verschijnen hier zodra facturen of uitgaven met btw-gesplitste grootboekposten zijn aangemaakt.',
            noSalesEntries: 'Geen omzetbelastingposten gevonden voor deze periode. Belastinggegevens verschijnen hier zodra facturen met btw-gesplitste grootboekposten zijn aangemaakt.',
            summaryTitle: '{{tax}}-betalingsoverzicht',
            amountDue: 'Te betalen aan Belastingdienst:',
            refundDue: 'Terug te ontvangen van Belastingdienst:',
            nilReturnAmount: 'Nettobedrag:',
            taxCollectedToRemit: 'Geïnde belasting af te dragen:',
            notOnReturnLabel: 'Overige mutaties op de rekening Te betalen omzetbelasting (niet in deze aangifte):',
            notOnReturnHint: 'Betalingen aan de belastingautoriteit en regels zonder verkooptarief. Getoond zodat dit rapport aansluit op de rekening Te betalen omzetbelasting; niet inbegrepen in de af te dragen belasting.',
            section: {
                sales_tax_payable: 'Verkoop- en gebruiksbelasting',
                meals_rooms_tax_payable: 'Belasting op maaltijden en overnachtingen',
                other: 'Overige belasting',
            },
            sectionWithCode: '{{title}} ({{code}})',
            category: { sales: 'Verkoop', meals: 'Maaltijden', rooms: 'Overnachtingen', alcohol: 'Alcohol', other: 'Overig' },
            categoryColumn: 'Categorie',
            sectionTotal: 'Totaal sectie',
            stateReturnTotal: 'Totale belasting op de aangifte van de staat',
            localOptionTitle: 'Lokale optionele belasting per gemeente',
            localOptionHint: 'Inbegrepen in de aangifte van de staat hierboven en daar per gemeente opgegeven.',
            townColumn: 'Gemeente',
            localTaxColumn: 'Lokale belasting',
            localOptionTotal: 'Totaal lokale optionele belasting',
            cityTitle: 'Betaald aan de stad {{city}}',
            cityHint: 'Geïnd voor de stad en rechtstreeks aan haar betaald. Niet in de aangifte van de staat.',
            cityTotal: 'Totaal betaald aan de stad',
            ofWhichStateReturn: 'waarvan op de aangifte van de staat',
            ofWhichCity: 'waarvan betaald aan de stad {{city}}',
            forPeriodEnding: 'Voor periode tot en met {{end}}',
            complianceLine1: 'Dit rapport is gegenereerd voor fiscale verantwoording.',
            complianceLine2: 'Controleer alle bedragen voordat u ze indient bij de Belastingdienst.',
        },
        volunteer: {
            title: 'Vrijwilligersrapport',
            subtitle: '{{project}} · {{from}} – {{to}}',
            signupPage: 'Aanmeldpagina: {{title}} · {{status}}',
            pageStatus: { draft: 'Concept', open: 'Open', closed: 'Gesloten' },
            noShifts: 'Dit project heeft nog geen vrijwilligersdiensten.',
            summary: {
                title: 'Samenvatting',
                people: 'Aangemelde vrijwilligers',
                peopleHint: 'Personen, elk één keer geteld',
                filled: 'Bezette plaatsen',
                filledHint: '{{pct}} van alle plaatsen',
                open: 'Open plaatsen',
                openHint: 'Diensten in totaal: {{count}}',
                empty: 'Diensten zonder bezetting',
                emptyHint: 'Volle diensten: {{count}}',
                confirmed: 'Bevestigd',
                pending: 'Wacht op goedkeuring',
                waitlisted: 'Op de wachtlijst',
                multiShift: 'Vrijwilligers met meer dan één dienst',
            },
            allRoles: 'Alle rollen',
            allDays: 'Alle dagen',
            byRole: {
                title: 'Per rol',
                caption: 'Bevestigde plaatsen ten opzichte van alle plaatsen',
                role: 'Rol',
                shifts: 'Diensten',
                full: 'Vol',
                filled: 'Bezet / plaatsen',
                fill: 'Bezetting',
                open: 'Open',
                waitlist: 'Wachtlijst',
            },
            byRoleAndShift: {
                title: 'Per rol en dienst',
                caption: 'Elke dienst, in tijdsvolgorde binnen de rol',
                shiftsCount: 'Diensten: {{count}} · {{confirmed}} / {{capacity}} bezet',
                day: 'Dag',
                time: 'Tijd',
                shift: 'Dienst',
                place: 'Plek',
                filled: 'Bezet / plaatsen',
                status: 'Status',
                full: 'Vol',
                noOneYet: 'Nog niemand',
                nOpen: '{{count}} open',
                nOnWaitlist: '{{count}} op de wachtlijst',
                nAwaitingApproval: '{{count}} in afwachting van goedkeuring',
                held: 'Gereserveerd voor goedkeuring',
                nextDay: '+1',
                nextDayNote: '+1 = eindigt volgende dag',
            },
            byDay: {
                title: 'Per dag',
                caption: 'Diensten gegroepeerd op de datum waarop ze beginnen',
                day: 'Dag',
                shifts: 'Diensten',
                volunteers: 'Vrijwilligers',
                filled: 'Bezet / plaatsen',
                fill: 'Bezetting',
                open: 'Open',
                lede: 'Vrijwilligers per dag telt elke persoon één keer die dag, ook als die twee diensten draait.',
            },
            matrix: {
                title: 'Rol × dag',
                caption: 'Bezet / plaatsen per rol per dag',
                role: 'Rol',
                legend: {
                    empty: 'Nog niemand',
                    under: 'Onder {{pct}}',
                    most: '{{pct}} of meer',
                    full: 'Vol',
                    none: 'Geen diensten die dag',
                },
            },
            notes: {
                countingRule: 'Bezet telt alleen bevestigde plaatsen. Geannuleerde plaatsen worden niet meegeteld. Plaatsen op de wachtlijst en plaatsen die op goedkeuring wachten staan apart; een plaats die op goedkeuring wacht, blijft gereserveerd en is dus niet open.',
            },
        },
    },
};
//# sourceMappingURL=nl.js.map